import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, Mail, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/App";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MagicLinkCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [status, setStatus] = useState("verifying"); // verifying, success, error
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState(null);
  
  const token = searchParams.get("token");
  const redirectParam = searchParams.get("redirect"); // URL parameter redirect takes priority
  
  useEffect(() => {
    if (!token) {
      setStatus("error");
      setError("No magic link token provided");
      return;
    }
    
    verifyMagicLink();
  }, [token]);
  
  const verifyMagicLink = async () => {
    try {
      setStatus("verifying");
      
      const res = await fetch(`${API}/auth/verify-magic-link`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
        credentials: "include"
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Invalid or expired magic link");
      }
      
      const data = await res.json();
      
      // Store session token
      localStorage.setItem("session_token", data.session_token);
      
      // Store pilot type for access control
      if (data.pilot_type) {
        localStorage.setItem("pilot_type", data.pilot_type);
      }
      
      // Update auth context
      login({
        user_id: data.user_id,
        email: data.email,
        name: data.name,
        username: data.username,
        phone: data.phone,
        role: data.role,
        pilot_type: data.pilot_type,
        wallet_id: data.wallet_id
      });
      
      setUserData(data);
      setStatus("success");
      
      // Redirect immediately. Using window.location.replace instead of
      // setTimeout(navigate, 2000) because the React Router setTimeout was
      // unreliable inside headless browsers used for automated screenshots
      // (the timer would never fire and the page would stay on /auth/magic).
      // URL parameter redirect takes priority over stored redirect_url.
      const finalRedirect = redirectParam || data.redirect_url || "/me";
      // Immediate redirect: setTimeout fires unreliably in headless browsers.
      // Real users still see the success screen flash for ~200ms before reload.
      window.location.replace(finalRedirect);
      
    } catch (err) {
      console.error("Magic link verification failed:", err);
      setStatus("error");
      setError(err.message);
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
        
        {status === "verifying" && (
          <div className="space-y-4">
            <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-gray-900 animate-spin" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900">
              Verifying your magic link...
            </h1>
            <p className="text-gray-600">
              Please wait while we sign you in
            </p>
          </div>
        )}
        
        {status === "success" && (
          <div className="space-y-4">
            <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900">
              {userData?.is_new_user ? "Welcome to Phygital!" : "Welcome back!"}
            </h1>
            <p className="text-gray-600">
              Signed in as {userData?.email}
            </p>
            <p className="text-sm text-gray-500">
              Redirecting you to your dashboard...
            </p>
          </div>
        )}
        
        {status === "error" && (
          <div className="space-y-4">
            <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900">
              Magic link failed
            </h1>
            <p className="text-gray-600">
              {error || "The link may have expired or already been used"}
            </p>
            
            <div className="pt-4 space-y-3">
              <p className="text-sm text-gray-500">
                During pilot mode, please request a new magic link from your host or admin.
              </p>
              
              <Button
                onClick={() => navigate("/")}
                variant="outline"
                className="w-full"
              >
                Return to Home
              </Button>
            </div>
          </div>
        )}
        
      </div>
    </div>
  );
};

export default MagicLinkCallback;
